import React, { useState, useRef, useEffect } from "react";
import Cropper from "react-cropper";
import { useAlert } from "../Components/AlertContext";
import { CustomSelect } from "./CustomFormElements";
import Button from "./Buttons";
import "cropperjs/dist/cropper.css";
import "./ImageUploader.css";

const ImageUploader = ({
    id,
    className = "",
    imageWidth = "150px",
    imageHeight = "150px",
    imageShape = "round", // Options: "round", "square", "rect"
    maxImageSizeMB = 1, // Max file size in MB
    allowedFormats = ["image/jpeg", "image/png"], // Allowed formats
    onImageChange, // Callback function for image update
    preuploadedImage // Image from DB
}) => {
    const PUBLIC_FILE_BASE = "https://t-api.obriy.ai/organization_api/v1/artifacts/public_file/";
    const [imageSrc, setImageSrc] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [aspectRatio, setAspectRatio] = useState(1); // Default: 1:1
    const [customWidth, setCustomWidth] = useState(300);
    const [customHeight, setCustomHeight] = useState(300);
    const cropperRef = useRef(null);
    const [cropperKey, setCropperKey] = useState(0);
    const { showAlert } = useAlert();


    // Max constraints for the exported avatar
    const EXPORT_MAX_SIDE = 384;   // cap longest side (shrink if you want even smaller files)
    const EXPORT_MAX_KB   = 50;    // hard cap in kilobytes

    const dataUrlBytes = (dataUrl) => {
    const base64 = (dataUrl.split(',')[1] || '');
    return Math.ceil((base64.length * 3) / 4);
    };

    // Downscale a canvas to fit within maxSide (preserves aspect)
    const downscaleCanvas = (srcCanvas, maxSide) => {
    const { width: w, height: h } = srcCanvas;
    const scale = Math.min(1, maxSide / Math.max(w, h));
    if (scale === 1) return srcCanvas;
    const dst = document.createElement('canvas');
    dst.width  = Math.max(1, Math.round(w * scale));
    dst.height = Math.max(1, Math.round(h * scale));
    const ctx = dst.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(srcCanvas, 0, 0, dst.width, dst.height);
    return dst;
    };

    // Compress a canvas to JPEG under EXPORT_MAX_KB, iteratively lowering quality & size
    const compressCanvasToDataUrl = (canvas, maxKB = EXPORT_MAX_KB) => {
    const MIME = 'image/jpeg';
    const maxBytes = maxKB * 1024;
    let work = canvas;
    let quality = 0.9;
    let dataUrl = work.toDataURL(MIME, quality);
    let bytes = dataUrlBytes(dataUrl);
    let guard = 0;

    while (bytes > maxBytes && guard < 16) {
        guard++;
        if (quality > 0.5) {
        quality = Math.max(0.5, quality - 0.1);
        } else {
        // reduce dimensions ~15% and retry with slightly higher quality
        const shrink = document.createElement('canvas');
        shrink.width  = Math.max(64, Math.round(work.width  * 0.85));
        shrink.height = Math.max(64, Math.round(work.height * 0.85));
        const sctx = shrink.getContext('2d');
        sctx.imageSmoothingEnabled = true;
        sctx.imageSmoothingQuality = 'high';
        sctx.drawImage(work, 0, 0, shrink.width, shrink.height);
        work = shrink;
        quality = 0.8;
        }
        dataUrl = work.toDataURL(MIME, quality);
        bytes = dataUrlBytes(dataUrl);
    }
    return dataUrl;
    };

    // Make a compressed dataURL from either a dataURL or an http(s) URL
    const makeCompressedFromSrc = (src, maxSide = EXPORT_MAX_SIDE, maxKB = EXPORT_MAX_KB) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
        const base = document.createElement('canvas');
        base.width = img.naturalWidth || img.width;
        base.height = img.naturalHeight || img.height;
        const ctx = base.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0);
        const down = downscaleCanvas(base, maxSide);
        resolve(compressCanvasToDataUrl(down, maxKB));
        };
        img.onerror = () => reject(new Error('Image load error'));
        img.src = src;
    });

    // useEffect(() => {
    //     if (!preuploadedImage) return;

    //     // If it's only an ID (no scheme), prefix with PUBLIC_FILE_BASE
    //     const isData = preuploadedImage.startsWith("data:image");
    //     const isHttp = /^https?:\/\//i.test(preuploadedImage);
    //     const resolvedSrc = isData
    //         ? preuploadedImage
    //         : (isHttp ? preuploadedImage : `${PUBLIC_FILE_BASE}${preuploadedImage}`);
    //     if (resolvedSrc.startsWith("data:image")) {
    //         setImageSrc(resolvedSrc); // Already Base64
    //         setTimeout(() => setCropperKey(k => k + 1), 200); // ✅ Refresh Cropper
    //         return;
    //     }

    //     const img = new Image();
    //     img.crossOrigin = "anonymous"; // Avoid CORS issues on canvas
    //     img.src = resolvedSrc;

    //     img.onload = () => {
    //         const canvas = document.createElement("canvas");
    //         canvas.width = img.width;
    //         canvas.height = img.height;
    //         const ctx = canvas.getContext("2d");
    //         ctx.drawImage(img, 0, 0);

    //         const base64String = canvas.toDataURL("image/png");
    //         setImageSrc(base64String);
    //         setTimeout(() => setCropperKey(k => k + 1), 200); // ✅ Refresh Cropper
    //     };

    //     img.onerror = () => {
    //         showAlert && showAlert("warning", "Failed to load preuploaded image.");
    //         setImageSrc(null);
    //     };
    // }, [preuploadedImage]);

    useEffect(() => {
    // If preuploaded was cleared, reset and notify parent with ""
    if (!preuploadedImage) {
        if (imageSrc !== null) setImageSrc(null);
        if (onImageChange) onImageChange("", id);
        return;
    }

    // Normalize ID → URL
    const isData = preuploadedImage.startsWith("data:image");
    const isHttp = /^https?:\/\//i.test(preuploadedImage);
    const resolvedSrc = isData
        ? preuploadedImage
        : (isHttp ? preuploadedImage : `${PUBLIC_FILE_BASE}${preuploadedImage}`);

    // Always compress (even if it's already data:image)
    makeCompressedFromSrc(resolvedSrc)
        .then((tiny) => {
        setImageSrc(tiny);
        setTimeout(() => setCropperKey(k => k + 1), 200);
        })
        .catch(() => {
        showAlert && showAlert("warning", "Failed to load preuploaded image.");
        setImageSrc(null);
        if (onImageChange) onImageChange("", id);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preuploadedImage]);
    

    useEffect(() => {
        if (imageSrc) {
            setTimeout(() => setCropperKey(prevKey => prevKey + 1), 200); // ✅ Ensure Cropper updates when image changes
        }
    }, [imageSrc]);
    
    const handleAspectRatioChange = (e) => {
        const selectedAspectRatio = e.target.value === "custom" ? "custom" : parseFloat(e.target.value);
        setAspectRatio(selectedAspectRatio);
        
        setTimeout(() => {
            if (cropperRef.current?.cropper) {
                cropperRef.current.cropper.setAspectRatio(selectedAspectRatio === "custom" ? NaN : selectedAspectRatio);
            }
        }, 100); // ✅ Immediately apply aspect ratio change
    };

    // Apply shape class dynamically
    const shapeClass = imageShape === "round" ? "image-round"
        : imageShape === "square" ? "image-square"
        : "image-rect"; // Default to rect

    // Handle Image Upload
    // const handleImageUpload = (file, uploaderId) => {
    //     if (!file) return;
    
    //     // Validate file size
    //     if (file.size / 1024 / 1024 > maxImageSizeMB) {
    //         showAlert && showAlert("warning", `File size exceeds ${maxImageSizeMB}MB limit.`);
    //         return;
    //     }
    
    //     // Validate file format
    //     if (!allowedFormats.includes(file.type)) {
    //         showAlert && showAlert("warning", `Invalid file format. Allowed: ${allowedFormats.join(", ")}`);
    //         return;
    //     }
    
    //     const reader = new FileReader();
    //     reader.readAsDataURL(file);
    //     reader.onloadend = () => {
    //         const base64Image = reader.result; // Ensure it's a string
    //         setImageSrc(base64Image);   
    
    //         if (onImageChange) {
    //             onImageChange(base64Image, uploaderId);
    //         }
    //     };
    // };
    const handleImageUpload = (file, uploaderId) => {
        if (!file) return;

        // Validate file size (raw file guard)
        if (file.size / 1024 / 1024 > maxImageSizeMB) {
            showAlert && showAlert("warning", `File size exceeds ${maxImageSizeMB}MB limit.`);
            return;
        }

        if (!allowedFormats.includes(file.type)) {
            showAlert && showAlert("warning", `Invalid file format. Allowed: ${allowedFormats.join(", ")}`);
            return;
        }

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = async () => {
            try {
            const rawDataUrl = reader.result; // large dataURL
            const tiny = await makeCompressedFromSrc(rawDataUrl); // ⬅ compress to ~≤50KB
            setImageSrc(tiny);
            if (onImageChange) onImageChange(tiny, uploaderId);
            } catch (e) {
            showAlert && showAlert("warning", "Failed to process image.");
            }
        };
    };

    const handleFileChange = (event, uploaderId) => {
        const file = event.target.files[0];
        if (!file) return;
    
        handleImageUpload(file, uploaderId);
    
        // ✅ Reset the specific file input to allow re-uploading the same image
        event.target.value = null;
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleDrop = (event) => {
        event.preventDefault();
        const file = event.dataTransfer.files[0];
        handleImageUpload(file);
    };

    // Save Cropped Image
   // Save Cropped Image (resized + compressed <= 100KB)
    // const saveCroppedImage = () => {
    // if (!cropperRef.current) return;
    // const cropper = cropperRef.current.cropper;

    // // Natural image size
    // const { naturalWidth, naturalHeight } = cropper.getImageData();

    // // Determine target aspect and initial target size
    // let targetW, targetH;
    // if (aspectRatio !== "custom") {
    //     const ratio = Number(aspectRatio) || 1;
    //     if (naturalWidth / naturalHeight > ratio) {
    //     targetH = naturalHeight;
    //     targetW = Math.round(targetH * ratio);
    //     } else {
    //     targetW = naturalWidth;
    //     targetH = Math.round(targetW / ratio);
    //     }
    // } else {
    //     targetW = Math.max(1, Number(customWidth) || 300);
    //     targetH = Math.max(1, Number(customHeight) || 300);
    // }

    // // First: get a cropped canvas at target size (no larger than the natural limits)
    // const initialCanvas = cropper.getCroppedCanvas({
    //     width: targetW,
    //     height: targetH,
    //     imageSmoothingEnabled: true,
    //     imageSmoothingQuality: "high",
    // });

    // // Second: downscale to the configured EXPORT_MAX_SIDE (caps pixel dimensions)
    // let canvas = downscaleCanvas(initialCanvas, EXPORT_MAX_SIDE);

    // // Third: compress to JPEG and iteratively reduce quality / scale to hit <= EXPORT_MAX_KB
    // const MIME = "image/jpeg";
    // let quality = 0.9; // start high
    // let dataUrl = canvas.toDataURL(MIME, quality);
    // let bytes = dataUrlBytes(dataUrl);
    // const maxBytes = EXPORT_MAX_KB * 1024;

    // // Reduce quality first; if that bottoms out, reduce dimensions and retry
    // let guard = 0;
    // while (bytes > maxBytes && guard < 12) {
    //     guard++;

    //     if (quality > 0.5) {
    //     quality = Math.max(0.5, quality - 0.1); // 0.9 → 0.8 → … → 0.5
    //     } else {
    //     // quality already low; shrink dimensions ~15% and reset quality
    //     const shrink = document.createElement("canvas");
    //     shrink.width  = Math.max(64, Math.round(canvas.width  * 0.85));
    //     shrink.height = Math.max(64, Math.round(canvas.height * 0.85));
    //     const sctx = shrink.getContext("2d");
    //     sctx.imageSmoothingEnabled = true;
    //     sctx.imageSmoothingQuality = "high";
    //     sctx.drawImage(canvas, 0, 0, shrink.width, shrink.height);
    //     canvas = shrink;
    //     quality = 0.8; // bump back up a bit after resizing
    //     }

    //     dataUrl = canvas.toDataURL(MIME, quality);
    //     bytes = dataUrlBytes(dataUrl);
    // }

    // // Finalize
    // setImageSrc(dataUrl);
    // if (onImageChange) onImageChange(dataUrl);
    // setIsEditing(false);
    // };

    // Save Cropped Image (resized + compressed ≤ EXPORT_MAX_KB)
    const saveCroppedImage = () => {
    if (!cropperRef.current) return;
    const cropper = cropperRef.current.cropper;

    // Target size based on aspect/custom settings
    const { naturalWidth, naturalHeight } = cropper.getImageData();
    let targetW, targetH;
    if (aspectRatio !== "custom") {
        const ratio = Number(aspectRatio) || 1;
        if (naturalWidth / naturalHeight > ratio) {
        targetH = naturalHeight;
        targetW = Math.round(targetH * ratio);
        } else {
        targetW = naturalWidth;
        targetH = Math.round(targetW / ratio);
        }
    } else {
        targetW = Math.max(1, Number(customWidth) || 300);
        targetH = Math.max(1, Number(customHeight) || 300);
    }

    // 1) Cropped canvas at target size
    const initialCanvas = cropper.getCroppedCanvas({
        width: targetW,
        height: targetH,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: "high",
    });

    // 2) Downscale to cap dimensions
    const down = downscaleCanvas(initialCanvas, EXPORT_MAX_SIDE);

    // 3) Compress to ≤ 50KB
    const tiny = compressCanvasToDataUrl(down, EXPORT_MAX_KB);

    setImageSrc(tiny);
    if (onImageChange) onImageChange(tiny, id);
    setIsEditing(false);
    };



    // Delete Image
    const deleteImage = (event) => {
        event.stopPropagation(); // Prevent file upload opening

        setImageSrc(null);

        // propagate empty value so parent onSave sends ""
        if (onImageChange) onImageChange("", id);

        // ✅ Reset the specific file input to allow re-uploading the same image
        const fileInput = document.getElementById(`file-input-${id}`);
        if (fileInput) {
            fileInput.value = null;
        }
    };

    return (
        <div 
            id={id}
            className={`image-uploader ${className} ${shapeClass}`} // ✅ Apply shape class
            style={{ width: imageWidth, height: imageHeight }}
            onDragOver={handleDragOver} 
            onDrop={handleDrop} 
            onClick={() => {
                if (!imageSrc) {
                    const fileInput = document.getElementById(`file-input-${id}`);
                    if (fileInput) {
                        fileInput.click();
                    }
                } 
            }}
        >
        <input id={`file-input-${id}`} type="file" accept="image/*"  onChange={(event) => handleFileChange(event, id)} hidden />

        {imageSrc ? (
            <div key={imageSrc} className={`image-container ${shapeClass}`} style={{ width: imageWidth, height: imageHeight }}>
                <img 
                    src={imageSrc} 
                    alt="Uploaded" 
                    className="uploaded-image" 
                    style={{ objectFit: "cover", width: "100%", height: "100%" }} 
                />
                <div className="hover-buttons">
                    <button className="edit-button" onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}><i className="fa-solid fa-pen-to-square"></i></button>
                    <button className="delete-button" onClick={deleteImage}><i className="fa-solid fa-trash-can"></i></button>
                </div>
            </div>
        ) : (
            <div className="upload-icon">
                <span><i className="fa-solid fa-camera-retro"></i></span>  
            </div>
        )}

        {/* Inline Image Editor */}
        {isEditing && imageSrc && (
            <div className="image-editor-overlay">
                <div className="aspect-ratio-selector d-flex flex-row align-center gap mb-1">
                    <label>Aspect Ratio:</label>
                    <div className="aspect-select">
                        <select value={aspectRatio} onChange={handleAspectRatioChange}>
                            <option value="1">1:1</option>
                            <option value="1.7778">16:9</option>
                            <option value="1.3333">4:3</option>
                            <option value="custom">Custom</option>
                        </select>
                    </div>
                </div>

                {/* ✅ Ensure Cropper initializes properly */}
                <Cropper
                    ref={cropperRef}
                    src={imageSrc}
                    style={{ width: "100%" }}
                    zoomTo={0}
                    initialAspectRatio={aspectRatio === "custom" ? NaN : aspectRatio}
                    aspectRatio={aspectRatio === "custom" ? NaN : aspectRatio}
                    preview=".img-preview"
                    viewMode={1}
                    minCropBoxHeight={50}
                    minCropBoxWidth={50}
                    background={false}
                    responsive={true}
                    autoCropArea={1}
                    checkOrientation={false}
                    guides={true}
                    movable={true}
                    cropBoxResizable={true}
                />

                <Button variant="primary" size="small" onClick={saveCroppedImage}>Save</Button>
                <Button variant="danger" size="small" onClick={() => setIsEditing(false)}>Cancel</Button>
            </div>
        )}
        </div>
    );
};

export default ImageUploader;